import { logException } from "@wss/error-tracking";
import Cookie from "js-cookie";
// eslint-disable-next-line import/no-unresolved
import { ThunkDispatch } from "StoreTypes";
import { useReCaptcha } from "@wss/re-captcha";
import Store from "../Store";
import { updateUserAndChatFromOrigin } from "../Store/actions/updateUserAndChatFromOrigin";
import { sendCustomerIoAccountEvent } from "~/analytics/CustomerIo/AccountEvents";
import loginStore from "./loginModalStore";

interface LoginProps {
  email: string;
  password: string;
  csrfToken: string;
  setIsAttemptingLogin: React.Dispatch<React.SetStateAction<boolean>>;
  setIsRedirecting: React.Dispatch<React.SetStateAction<boolean>>;
  setErrors: React.Dispatch<React.SetStateAction<ErrorStates>>;
  reCaptcha: ReturnType<typeof useReCaptcha>;
}

interface RequestEmailLoginLinkProps {
  email: string;
  csrfToken: string;
  errors: ErrorStates;
  setErrors: React.Dispatch<React.SetStateAction<ErrorStates>>;
  setIsRequestingEmailLoginLink: React.Dispatch<React.SetStateAction<boolean>>;
  setIsEmailLoginLinkSent: React.Dispatch<React.SetStateAction<boolean>>;
  reCaptcha: ReturnType<typeof useReCaptcha>;
}

type ErrorStates = {
  hasLoginError: boolean;
  hasCaptchaError: boolean;
  hasLoginEmailLinkError: boolean;
  hasServerError: boolean;
};

const mergeCartUrl = "/myaccount/mergecart?sessionGuidFromSessionCart=";
const requestEmailLoginLinkUrl = "/myaccount:login/requestemailloginlink/";
const loginUrl = "/myaccount:login/login/";

export const getDefaultErrorStates = () => ({
  hasLoginError: false,
  hasCaptchaError: false,
  hasLoginEmailLinkError: false,
  hasServerError: false,
});

const buildFetchParameters = (body: string): RequestInit => ({
  method: "POST",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
  body,
  credentials: "include",
});

export const buildLoginParameters = async (
  email: string,
  password: string,
  csrfToken: string,
  captchaTokenVal: string,
): Promise<RequestInit> => {
  try {
    const requestBody = JSON.stringify({
      email,
      password,
      csrfToken,
      captchaToken: captchaTokenVal,
    });
    return buildFetchParameters(requestBody);
  } catch (error) {
    logException(error);
    throw new Error("Failed to build login parameters");
  }
};

export const buildRequestEmailLoginLinkParameters = async (
  email: string,
  csrfToken: string,
  captchaTokenVal: string,
): Promise<RequestInit> => {
  try {
    const requestBody = JSON.stringify({
      email,
      csrfToken,
      captchaToken: captchaTokenVal,
    });

    return buildFetchParameters(requestBody);
  } catch (error) {
    logException(error);
    throw new Error("Failed to build request email login link parameters");
  }
};

export const sendRequest = async (
  url: string,
  parameters: RequestInit,
  isLoginEmailLinkRequest?: boolean,
) => {
  const results = {
    success: false,
    errors: getDefaultErrorStates(),
    warnings: {
      cartsNeedMerged: false,
    },
  };

  try {
    const response = await fetch(url, parameters);

    if (response.ok) {
      const data: {
        success: boolean;
        warnings: string[];
        errors: string[];
      } = await response.json();
      if (data.success) {
        results.success = true;
        results.warnings.cartsNeedMerged =
          data.warnings.includes("CartsNeedMerged");
      } else {
        results.errors.hasLoginError =
          !isLoginEmailLinkRequest &&
          (data.errors.includes("InvalidEmail") ||
            data.errors.includes("InvalidPassword") ||
            data.errors.includes("InvalidCsrf") ||
            data.errors.includes("InvalidCredentials"));
        results.errors.hasCaptchaError = data.errors.includes("InvalidCaptcha");
        results.errors.hasLoginEmailLinkError = isLoginEmailLinkRequest
          ? data.errors.includes("InvalidLoginEmailLinkCaptcha") ||
            data.errors.includes("InvalidLoginEmailLink") ||
            data.errors.includes("InvalidEmail")
          : false;
      }
    } else {
      results.errors.hasServerError = true;
      logException(new Error(`Request failed with status ${response.status}`));
    }
  } catch (error) {
    results.errors.hasServerError = true;
    logException(error);
  }

  return results;
};

export const handleLoginSucceeded = (cartsNeedMerged: boolean) => {
  const { targetUrl, queryParams, callback } =
    loginStore.getState().options || {};
  const sessionGuid = Cookie.get("SESSION_ID");
  const safeQueryParams = queryParams ?? "";
  if (cartsNeedMerged && sessionGuid) {
    window.location.href = `${mergeCartUrl + sessionGuid}${
      targetUrl
        ? `&redirect=${encodeURIComponent(targetUrl + safeQueryParams)}`
        : ""
    }`;
  } else if (callback) {
    (Store.dispatch as ThunkDispatch)(updateUserAndChatFromOrigin());
    callback();
  } else if (targetUrl) {
    window.location.href = targetUrl + safeQueryParams;
  } else if (queryParams) {
    window.location.href =
      window.location?.href.split("?")[0] + safeQueryParams;
  } else {
    window.location.reload();
  }
};
export const requestEmailLoginLink = async (
  props: RequestEmailLoginLinkProps,
) => {
  const {
    email,
    csrfToken,
    setErrors,
    setIsRequestingEmailLoginLink,
    setIsEmailLoginLinkSent,
    reCaptcha,
  } = props;

  const captchaToken = await reCaptcha.execute();
  if (captchaToken.isError) throw captchaToken.error;

  const fetchParameters = await buildRequestEmailLoginLinkParameters(
    email,
    csrfToken,
    captchaToken.value,
  );

  const results = await sendRequest(
    requestEmailLoginLinkUrl,
    fetchParameters,
    true,
  );

  setErrors(results.errors);
  setIsRequestingEmailLoginLink(false);
  setIsEmailLoginLinkSent(results.success);
};

export const login = async (props: LoginProps) => {
  const {
    email,
    password,
    csrfToken,
    setIsAttemptingLogin,
    setIsRedirecting,
    setErrors,
    reCaptcha,
  } = props;

  const captchaToken = await reCaptcha.execute();
  if (captchaToken.isError) throw captchaToken.error;

  const fetchParameters = await buildLoginParameters(
    email,
    password,
    csrfToken,
    captchaToken.value,
  );

  const results = await sendRequest(loginUrl, fetchParameters);
  if (results.success) {
    sendCustomerIoAccountEvent("account_login", email);
    handleLoginSucceeded(results.warnings.cartsNeedMerged);
  }

  setIsAttemptingLogin(false);
  setErrors(results.errors);
  setIsRedirecting(results.success);
};
